/**
 * Analyze tab — Structural Loads panel tools (BIM "Structural Loads").
 *
 * Loads are attached to the analytical model that FEASolver consumes
 * (structuralLoadManager) AND mirrored as BIMElements with category
 * 'Structural Loads' so they are selectable and render as BIM-style red
 * arrows in the viewport.
 * - Point Load: click an analytical node.
 * - Line Load: click two points along a member axis.
 * - Area Load: click a closed polygon over a floor (Enter/click start to finish).
 * - Load Cases & Combinations: dialog tool (openManageDialog).
 */

import { v4 as uuidv4 } from 'uuid';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { BaseTool } from '../BaseTool';
import { analyticalModelManager, structuralLoadManager } from '../../core/services';
import { useAnalysisStore } from '../../state/useAnalysisStore';
import i18n from '../../i18n';

/** Shared default load case used when the Options bar selection is empty. */
function resolveLoadCaseId(): string | null {
    const name = useAnalysisStore.getState().activeLoadCaseName
        || 'Dead';
    const lc = structuralLoadManager.getLoadCaseByName(name);
    return lc ? lc.id : structuralLoadManager.getAllLoadCases()[0]?.id ?? null;
}

export class LoadCaseTool extends BaseTool {
    id = 'ANALYZE_LOAD_CASES';
    name = 'Load Cases & Combinations';
    cursor = 'default';

    onActivate(): void {
        this.context?.appStore.openManageDialog('analyzeLoadCases');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}

export class PointLoadTool extends BaseTool {
    id = 'ANALYZE_POINT_LOAD';
    name = 'Point Load';
    cursor = 'crosshair';

    onActivate(): void {
        this.notify(i18n.t('tools.notify.clickAnAnalyticalNodeToApply'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const pos: [number, number, number] = [point.x, point.y, point.z];
        const node = analyticalModelManager.findNodeAtPosition(pos, 0.3);
        if (!node) {
            this.notify(i18n.t('tools.notify.pointLoadsMustBePlacedOn'), 'warning');
            return;
        }
        const caseId = resolveLoadCaseId();
        if (!caseId) {
            this.notify(i18n.t('tools.notify.noLoadCaseExistsOpenLoad'), 'error');
            return;
        }
        const magnitude = this.context.bimStore.toolProperties.pointLoadMagnitude ?? -1000;
        structuralLoadManager.addPointLoad(caseId, node.id, { fz: magnitude });

        const caseName = structuralLoadManager.getLoadCase(caseId)?.name ?? 'Dead';
        this.context.bimStore.addElement({
            id: uuidv4(),
            category: 'Structural Loads',
            type: 'Point Load',
            name: `Point Load ${caseName}`,
            parameters: { 'Load Case': caseName, 'Magnitude (N)': magnitude },
            constraints: [],
            geometryData: {
                loadType: 'Point',
                position: node.position,
                nodeId: node.id,
                caseId,
                magnitude,
            },
        } as any);
        useAnalysisStore.getState().bumpLoads();
        this.notify(i18n.t('tools.notify.pointLoadApplied', { magnitude: magnitude, caseName: caseName }), 'success');
        this.context.appStore.setActiveTool('select');
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const node = analyticalModelManager.findNodeAtPosition([point.x, point.y, point.z], 0.3);
        if (node) {
            this.context.appStore.setToolPreview({
                type: 'point',
                position: new THREE.Vector3(...node.position),
                color: '#d23f31',
            });
        } else {
            this.clearPreview();
        }
    }

    onPointerUp(): void { }
}

export class LineLoadTool extends BaseTool {
    id = 'ANALYZE_LINE_LOAD';
    name = 'Line Load';
    cursor = 'crosshair';

    private startPoint: THREE.Vector3 | null = null;

    onActivate(): void {
        this.notify(i18n.t('tools.notify.clickTwoPointsAlongAMember'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.startPoint) {
            this.startPoint = snapped;
            this.updatePreview(snapped);
            return;
        }
        const caseId = resolveLoadCaseId();
        if (!caseId) {
            this.notify(i18n.t('tools.notify.noLoadCaseExistsOpenLoad2'), 'error');
            return;
        }
        const magnitude = this.context.bimStore.toolProperties.lineLoadMagnitude ?? -1000;
        const memberId = this.findNearestMemberId(snapped);
        if (memberId) {
            structuralLoadManager.addDistributedLoad(caseId, memberId, {
                startMagnitude: magnitude,
                endMagnitude: magnitude,
                axis: 'Z',
            });
        } else {
            this.notify(i18n.t('tools.notify.noAnalyticalMemberFoundNearThe'), 'warning');
        }
        const caseName = structuralLoadManager.getLoadCase(caseId)?.name ?? 'Dead';
        this.context.bimStore.addElement({
            id: uuidv4(),
            category: 'Structural Loads',
            type: 'Line Load',
            name: `Line Load ${caseName}`,
            parameters: { 'Load Case': caseName, 'Magnitude (N/m)': magnitude },
            constraints: [],
            geometryData: {
                loadType: 'Line',
                start: [this.startPoint.x, this.startPoint.y, this.startPoint.z],
                end: [snapped.x, snapped.y, snapped.z],
                memberId,
                caseId,
                magnitude,
            },
        } as any);
        useAnalysisStore.getState().bumpLoads();
        this.notify(i18n.t('tools.notify.lineLoadApplied', { magnitude: magnitude, caseName: caseName }), 'success');
        this.startPoint = null;
        this.clearPreview();
        this.context.appStore.setActiveTool('select');
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.startPoint) return;
        const point = this.getIntersection(event);
        if (point) this.updatePreview(this.snapToGrid(point));
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.startPoint = null;
            this.clearPreview();
        }
        super.onKeyDown(event);
    }

    private updatePreview(current: THREE.Vector3): void {
        if (!this.startPoint) return;
        this.context?.appStore.setToolPreview({
            type: 'line',
            start: this.startPoint,
            end: current,
            color: '#d23f31',
        });
    }

    /** Nearest analytical member (endpoint distance) for attaching the load. */
    private findNearestMemberId(point: THREE.Vector3): string | null {
        let best: string | null = null;
        let bestDist = 0.5;
        for (const member of analyticalModelManager.getMembers()) {
            const nodes = analyticalModelManager.getNodes();
            const s = nodes.find(n => n.id === member.startNodeId);
            const e = nodes.find(n => n.id === member.endNodeId);
            if (!s || !e) continue;
            const seg = new THREE.Line3(
                new THREE.Vector3(...s.position),
                new THREE.Vector3(...e.position)
            );
            const closest = new THREE.Vector3();
            seg.closestPointToPoint(point, true, closest);
            const d = closest.distanceTo(point);
            if (d < bestDist) {
                bestDist = d;
                best = member.id;
            }
        }
        return best;
    }

    onPointerUp(): void { }
}

export class AreaLoadTool extends BaseTool {
    id = 'ANALYZE_AREA_LOAD';
    name = 'Area Load';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];

    onActivate(): void {
        this.notify(i18n.t('tools.notify.clickPolygonPointsOverTheLoaded'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        // Close the loop when clicking near the start point.
        if (this.points.length >= 3 && snapped.distanceTo(this.points[0]) < 0.3) {
            this.finish();
            return;
        }
        this.points.push(snapped);
        this.updatePreview(snapped);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || this.points.length === 0) return;
        const point = this.getIntersection(event);
        if (point) this.updatePreview(this.snapToGrid(point));
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 3) {
            this.finish();
            return;
        }
        super.onKeyDown(event);
    }

    private finish(): void {
        const ctx = this.context;
        if (!ctx || this.points.length < 3) return;
        const caseId = resolveLoadCaseId();
        if (!caseId) {
            this.notify(i18n.t('tools.notify.noLoadCaseExistsOpenLoad3'), 'error');
            return;
        }
        const magnitude = ctx.bimStore.toolProperties.areaLoadMagnitude ?? -1000;
        const boundary = this.points.map(p => [p.x, p.y, p.z] as [number, number, number]);

        // Attach to the nearest analytical surface whose XY bbox contains the
        // polygon centroid, so FEA picks it up.
        const centroid = boundary.reduce(
            (acc, p) => [acc[0] + p[0] / boundary.length, acc[1] + p[1] / boundary.length, acc[2]],
            [0, 0, boundary[0][2]]
        );
        let surfaceId: string | null = null;
        let bestDist = Infinity;
        const nodes = analyticalModelManager.getNodes();
        for (const surface of analyticalModelManager.getSurfaces()) {
            const pts = surface.boundaryNodeIds
                .map(id => nodes.find(n => n.id === id)?.position)
                .filter(Boolean) as [number, number, number][];
            if (pts.length < 3) continue;
            const c = pts.reduce((a, p) => [a[0] + p[0] / pts.length, a[1] + p[1] / pts.length, a[2]], [0, 0, 0]);
            const d = Math.hypot(c[0] - centroid[0], c[1] - centroid[1]);
            if (d < bestDist) {
                bestDist = d;
                surfaceId = surface.id;
            }
        }
        if (surfaceId && bestDist < 5) {
            structuralLoadManager.addAreaLoad(caseId, surfaceId, magnitude);
        }

        const caseName = structuralLoadManager.getLoadCase(caseId)?.name ?? 'Dead';
        ctx.bimStore.addElement({
            id: uuidv4(),
            category: 'Structural Loads',
            type: 'Area Load',
            name: `Area Load ${caseName}`,
            parameters: { 'Load Case': caseName, 'Magnitude (N/m²)': magnitude },
            constraints: [],
            geometryData: {
                loadType: 'Area',
                boundary,
                surfaceId,
                caseId,
                magnitude,
            },
        } as any);
        useAnalysisStore.getState().bumpLoads();
        this.notify(i18n.t('tools.notify.areaLoadApplied', { magnitude: magnitude, points: boundary.length, caseName: caseName }), 'success');
        this.points = [];
        this.clearPreview();
        ctx.appStore.setActiveTool('select');
    }

    private updatePreview(current: THREE.Vector3): void {
        this.context?.appStore.setToolPreview({
            type: 'polygon',
            points: [...this.points.map(p => new THREE.Vector3(p.x, p.y, p.z)), current],
            color: '#e2622e',
        });
    }

    onPointerUp(): void { }
}

/** Toggle visibility of load symbols in the viewport. */
export class ShowLoadsTool extends BaseTool {
    id = 'ANALYZE_SHOW_LOADS';
    name = 'Show Loads';
    cursor = 'default';

    onActivate(): void {
        const store = useAnalysisStore.getState();
        store.toggleLoads();
        this.notify(i18n.t('tools.notify.loadsDisplayToggled', { state: store.showLoads ? i18n.t('tools.notify.stateDisabled') : i18n.t('tools.notify.stateEnabled') }), 'info');
        this.context?.appStore.setActiveTool('select');
    }

    onPointerDown(): void { }
    onPointerMove(): void { }
    onPointerUp(): void { }
}
